import { useEffect, useRef } from 'react';

/**
 * Custom hook for handling copy events with enhanced formatting preservation
 */
export const useCopyHandler = <T extends HTMLElement = HTMLDivElement>() => {
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const handleCopy = (event: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return;
      }

      const range = selection.getRangeAt(0);

      // Check if the selection contains any mention elements
      const selectionContainer = range.cloneContents();
      const mentionElements = selectionContainer.querySelectorAll('a.mention');

      if (mentionElements.length > 0) {
        // If we have mentions in selection, preserve their HTML structure
        const div = document.createElement('div');
        div.appendChild(selectionContainer);
        const htmlContent = div.innerHTML;
        const textContent = selection.toString();

        event.clipboardData?.setData('text/html', htmlContent);
        event.clipboardData?.setData('text/plain', textContent);
        event.preventDefault();
        return;
      }

      // Check if selection is inside a formatted element within this content
      let currentElement: Node | null = range.commonAncestorContainer;
      if (currentElement.nodeType === Node.TEXT_NODE) {
        currentElement = currentElement.parentElement!;
      }

      // Walk up the DOM tree to find formatting elements
      while (currentElement && currentElement !== element) {
        const nodeName = currentElement.nodeName.toLowerCase();

        // Handle mention formatting - preserve mention structure
        if (nodeName === 'a' && (currentElement as HTMLElement).classList?.contains('mention')) {
          const selectedText = selection.toString();
          const mentionElement = currentElement as HTMLElement;

          if (selectedText) {
            // Check if the selection is entirely within this mention
            const mentionText = mentionElement.textContent || '';
            if (mentionText.includes(selectedText)) {
              // Preserve the entire mention structure
              const mentionHTML = mentionElement.outerHTML;

              event.clipboardData?.setData('text/html', mentionHTML);
              event.clipboardData?.setData('text/plain', selectedText);
              event.preventDefault();
              return;
            }
          }
        }

        // Handle blockquote formatting
        if (nodeName === 'blockquote') {
          const selectedText = selection.toString();
          if (selectedText) {
            const wrappedHTML = `<blockquote><p>${selectedText.replace(/\n/g, '</p><p>')}</p></blockquote>`;

            event.clipboardData?.setData('text/html', wrappedHTML);
            event.clipboardData?.setData('text/plain', selectedText);
            event.preventDefault();
            return;
          }
        }

        currentElement = currentElement.parentNode;
      }
    };

    element.addEventListener('copy', handleCopy);

    return () => {
      element.removeEventListener('copy', handleCopy);
    };
  }, []);

  return elementRef;
};
